<script setup lang="ts">
import 'swiper/css'

import useProductStore from '@/stores/product'
import Icon from 'lilasia-icons'
import { storeToRefs } from 'pinia'
import type { Swiper } from 'swiper/types'
import { Swiper as AppSwiper, SwiperSlide } from 'swiper/vue'
import { computed, ref } from 'vue'
import FadeTransition from '../Transitions/FadeTransition.vue'
import AppImage from '../common/AppImage.vue'
import OfficialMerchantBadge from '../common/OfficialMerchantBadge.vue'
import ProductGallery from './ProductGallery.vue'

const props = defineProps<{
  product: Product
}>()

const productStore = useProductStore()

const { activeImageIndex } = storeToRefs(productStore)
const swiper = ref<Swiper | null>(null)
const showProductGallery = ref(false)

const images = computed(() => {
  return props.product.images
})

const activeImage = computed(() => {
  return images.value?.[activeImageIndex.value]
})

const setSwiper = (instance: Swiper) => {
  swiper.value = instance
}

const setActiveImage = (index: number) => {
  activeImageIndex.value = -1
  setTimeout(() => {
    activeImageIndex.value = index
  }, 100)
  swiper.value?.slideTo(index)
}
</script>

<template>
  <div class="space-y-16">
    <div class="relative">
      <FadeTransition>
        <AppImage
          v-if="activeImage"
          :src="activeImage.image_url"
          :alt="product.name"
          :width="723"
          :height="723"
          class="aspect-square w-full cursor-pointer rounded-16 bg-white object-cover"
          @click="showProductGallery = true"
        />
      </FadeTransition>

      <!-- Official Merchant Badge -->
      <div
        v-if="product.shop"
        class="absolute bottom-0 right-0 z-10"
      >
        <OfficialMerchantBadge
          class="w-fit"
          rounded-class="rounded-tl-24 md:rounded-br-[13px] lg:rounded-br-[14px]"
          large
        />
      </div>
    </div>

    <!-- Thumbnails -->
    <AppSwiper
      v-if="images?.length"
      :space-between="16"
      :slides-per-view="6"
      class="relative"
      @swiper="setSwiper"
    >
      <SwiperSlide
        v-for="(image, index) in images"
        :key="index"
        role="button"
        @click="setActiveImage(index)"
      >
        <AppImage
          v-if="image.thumbnail_url"
          :src="image.thumbnail_url"
          :alt="product.name"
          :width="107"
          :height="107"
          :class="{
            'h-full w-full rounded-8 bg-white transition': true,
            'border-2 border-blue-100': activeImageIndex === index,
            'border-2 border-blue-10': activeImageIndex !== index
          }"
        />
      </SwiperSlide>

      <!-- Navigation -->
      <FadeTransition group>
        <div
          v-if="!swiper?.isBeginning"
          class="absolute left-4 top-1/2 z-10 -translate-y-1/2"
        >
          <button
            class="flex size-48 items-center justify-center rounded-9999 border-2 border-black-10 bg-white text-black-60"
            aria-label="Slide previous"
            @click="swiper?.slidePrev()"
          >
            <Icon
              name="keyboard_arrow_left"
              size="48"
            />
          </button>
        </div>

        <div
          v-if="!swiper?.isEnd"
          class="absolute right-4 top-1/2 z-10 -translate-y-1/2"
        >
          <button
            class="flex size-48 items-center justify-center rounded-9999 border-2 border-black-10 bg-white text-black-60"
            aria-label="Slide Next"
            @click="swiper?.slideNext()"
          >
            <Icon
              name="keyboard_arrow_right"
              size="48"
            />
          </button>
        </div>
      </FadeTransition>
    </AppSwiper>

    <Teleport to="body">
      <ProductGallery
        v-model="showProductGallery"
        :product="product"
        :image-index="activeImageIndex"
      />
    </Teleport>
  </div>
</template>
